import { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { ShoppingBag } from 'lucide-react';
import type { Order } from '@/domain/entities/order.entity';
import { OrderCard } from './OrderCard';
import { separateOrders } from './utils';
import { MAX_ACTIVE_PER_PAGE, MAX_PAST_PER_PAGE } from './constants';

// --- Styled components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space['2xl']};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const CountBadge = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
`;

const EmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.space.lg} 0;
  max-width: 360px;
`;

const ShopButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  background: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: white;
  cursor: pointer;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.lg} 0;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ active?: boolean }>`
  min-width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ active, theme }) => (active ? theme.colors.text.primary : theme.colors.border.color)};
  background: ${({ active, theme }) =>
    active ? theme.colors.text.primary : theme.colors.background.primary};
  color: ${({ active, theme }) =>
    active ? theme.colors.background.primary : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  font-family: inherit;
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not([disabled]) {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Loading = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #c53030;
`;

const Separator = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.color};
`;

// --- Component ---

interface PurchaseHistoryProps {
  readonly orders: Order[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly locale?: string;
  readonly currencyCode?: string;
  readonly currencySymbol?: string;
  readonly onViewDetails: (orderId: string) => void;
  readonly onStartShopping?: () => void;
}

export function PurchaseHistory({
  orders,
  isLoading,
  error,
  locale = 'en-US',
  currencyCode = 'USD',
  currencySymbol,
  onViewDetails,
  onStartShopping,
}: PurchaseHistoryProps) {
  const [activePage, setActivePage] = useState(1);
  const [pastPage, setPastPage] = useState(1);

  const { active, past } = useMemo(() => separateOrders(orders), [orders]);

  const activePageCount = Math.max(1, Math.ceil(active.length / MAX_ACTIVE_PER_PAGE));
  const pastPageCount = Math.max(1, Math.ceil(past.length / MAX_PAST_PER_PAGE));

  const pagedActive = active.slice(
    (activePage - 1) * MAX_ACTIVE_PER_PAGE,
    activePage * MAX_ACTIVE_PER_PAGE,
  );
  const pagedPast = past.slice(
    (pastPage - 1) * MAX_PAST_PER_PAGE,
    pastPage * MAX_PAST_PER_PAGE,
  );

  if (isLoading) {
    return <Loading>Loading orders...</Loading>;
  }

  if (error) {
    return <ErrorText>{error}</ErrorText>;
  }

  if (orders.length === 0) {
    return (
      <EmptyCard>
        <EmptyIcon>
          <ShoppingBag size={28} />
        </EmptyIcon>
        <EmptyText>
          You haven&apos;t made any purchases yet. Browse our collections and find something you love!
        </EmptyText>
        {onStartShopping && (
          <ShopButton onClick={onStartShopping}>Start Shopping</ShopButton>
        )}
      </EmptyCard>
    );
  }

  return (
    <Container>
      {/* Active Orders */}
      {active.length > 0 && (
        <div>
          <SectionHeader>
            <SectionTitle>
              Active Orders <CountBadge>({active.length})</CountBadge>
            </SectionTitle>
          </SectionHeader>
          {pagedActive.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              locale={locale}
              currencyCode={currencyCode}
              currencySymbol={currencySymbol}
              onViewDetails={onViewDetails}
            />
          ))}
          {activePageCount > 1 && (
            <PaginationRow>
              <PageButton disabled={activePage <= 1} onClick={() => setActivePage((p) => p - 1)}>
                &laquo;
              </PageButton>
              {Array.from({ length: activePageCount }, (_, i) => (
                <PageButton
                  key={i}
                  active={activePage === i + 1}
                  onClick={() => setActivePage(i + 1)}
                >
                  {i + 1}
                </PageButton>
              ))}
              <PageButton
                disabled={activePage >= activePageCount}
                onClick={() => setActivePage((p) => p + 1)}
              >
                &raquo;
              </PageButton>
            </PaginationRow>
          )}
        </div>
      )}

      {active.length > 0 && past.length > 0 && <Separator />}

      {/* Past Orders */}
      {past.length > 0 && (
        <div>
          <SectionHeader>
            <SectionTitle>
              Past Orders <CountBadge>({past.length})</CountBadge>
            </SectionTitle>
          </SectionHeader>
          {pagedPast.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              locale={locale}
              currencyCode={currencyCode}
              currencySymbol={currencySymbol}
              onViewDetails={onViewDetails}
            />
          ))}
          {pastPageCount > 1 && (
            <PaginationRow>
              <PageButton disabled={pastPage <= 1} onClick={() => setPastPage((p) => p - 1)}>
                &laquo;
              </PageButton>
              {Array.from({ length: pastPageCount }, (_, i) => (
                <PageButton
                  key={i}
                  active={pastPage === i + 1}
                  onClick={() => setPastPage(i + 1)}
                >
                  {i + 1}
                </PageButton>
              ))}
              <PageButton
                disabled={pastPage >= pastPageCount}
                onClick={() => setPastPage((p) => p + 1)}
              >
                &raquo;
              </PageButton>
            </PaginationRow>
          )}
        </div>
      )}

      {/* Edge case: all orders fell into 'none' category */}
      {active.length === 0 && past.length === 0 && orders.length > 0 && (
        <EmptyCard>
          <EmptyText>All your orders are being processed.</EmptyText>
        </EmptyCard>
      )}
    </Container>
  );
}
